import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaTag, FaCheckCircle, FaTimes } from 'react-icons/fa';

const OrderSummary = ({
                          items = [],
                          subtotal = 0,
                          shipping = 0,
                          tax = 0,
                          total = 0,
                          discount = 0,
                          coupon = null,
                          appliedOffers = [],
                          onApplyCoupon,
                          onRemoveCoupon,
                          isLoading = false,
                          onPlaceOrder
                      }) => {
    const [couponCode, setCouponCode] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponError, setCouponError] = useState('');

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;

        setCouponLoading(true);
        setCouponError('');

        try {
            await onApplyCoupon(couponCode);
            setCouponCode('');
        } catch (error) {
            setCouponError(error.message || 'Failed to apply coupon');
        } finally {
            setCouponLoading(false);
        }
    };

    return (
        <div className="sticky top-24">
            <div className="bg-white rounded-2xl shadow-luxury p-6">
                <h2 className="font-heading text-xl font-medium text-charcoal mb-6">
                    Order Summary
                </h2>

                {/* Order Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                    {items.map((item, index) => (
                        <div key={item._id || index} className="flex items-center gap-3">
                            <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                                <img
                                    src={item.image || item.product?.images?.[0]}
                                    alt={item.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-charcoal text-sm truncate">{item.name}</h4>
                                <p className="text-xs text-charcoal/50">
                                    {item.variant?.name} • Qty: {item.quantity}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="font-medium text-charcoal">
                                    ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Applied Offers */}
                {appliedOffers.length > 0 && (
                    <div className="mb-4 space-y-2">
                        <p className="text-sm font-medium text-charcoal">Applied Offers</p>
                        {appliedOffers.map((offer, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-2 bg-sage/10 rounded-lg"
                            >
                                <div className="flex items-center gap-2">
                                    <FaTag className="w-3 h-3 text-sage" />
                                    <span className="text-sm font-medium text-sage">
                                        {offer.offer?.code || offer.code}
                                    </span>
                                </div>
                                <span className="font-medium text-sage">
                                    -${(offer.discountAmount || 0).toFixed(2)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Coupon Section */}
                <div className="mb-6">
                    {coupon ? (
                        <div className="flex items-center justify-between p-3 bg-sage/10 border border-sage/30 rounded-xl">
                            <div className="flex items-center gap-2">
                                <FaCheckCircle className="w-4 h-4 text-sage" />
                                <span className="text-sm font-medium text-sage">
                                    Coupon: {coupon.code}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-sage">
                                    -${coupon.discount.toFixed(2)}
                                </span>
                                <button
                                    onClick={onRemoveCoupon}
                                    className="p-1 hover:bg-sage/20 rounded-full"
                                >
                                    <FaTimes className="w-3 h-3 text-sage" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <input
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                                placeholder="Enter coupon code"
                                className="flex-1 px-4 py-3 bg-nude/20 border border-nude/30 rounded-xl text-sm placeholder:text-charcoal/40 focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/30"
                            />
                            <button
                                onClick={handleApplyCoupon}
                                disabled={!couponCode.trim() || couponLoading}
                                className="px-4 py-3 bg-champagne text-white rounded-xl font-medium text-sm hover:bg-champagne/90 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {couponLoading ? '...' : 'Apply'}
                            </button>
                        </div>
                    )}
                    {couponError && (
                        <p className="text-xs text-error mt-2">{couponError}</p>
                    )}
                </div>

                {/* Price Breakdown */}
                <div className="space-y-3 border-t border-nude/30 pt-4">
                    <div className="flex justify-between">
                        <span className="text-charcoal/70">Subtotal</span>
                        <span className="font-medium text-charcoal">${subtotal.toFixed(2)}</span>
                    </div>

                    {discount > 0 && (
                        <div className="flex justify-between">
                            <span className="text-charcoal/70">Discount</span>
                            <span className="font-medium text-green-600">-${discount.toFixed(2)}</span>
                        </div>
                    )}

                    <div className="flex justify-between">
                        <span className="text-charcoal/70">Shipping</span>
                        <span className="font-medium text-charcoal">
                            {shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}
                        </span>
                    </div>

                    <div className="flex justify-between">
                        <span className="text-charcoal/70">Tax</span>
                        <span className="font-medium text-charcoal">${tax.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between pt-4 border-t border-nude/30">
                        <span className="text-lg font-medium text-charcoal">Total</span>
                        <span className="text-2xl font-light text-charcoal">${total.toFixed(2)}</span>
                    </div>
                </div>

                {/* Place Order Button */}
                <motion.button
                    onClick={onPlaceOrder}
                    disabled={isLoading || items.length === 0}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full mt-6 px-8 py-4 rounded-full bg-gradient-to-r from-charcoal via-charcoal/95 to-charcoal/90 text-pearl font-medium text-lg hover:from-charcoal/90 hover:via-charcoal/80 hover:to-charcoal/70 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
                >
                    <span className="relative flex items-center justify-center gap-3">
                        {isLoading ? (
                            <>
                                <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                                Processing...
                            </>
                        ) : (
                            'Place Order'
                        )}
                    </span>
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"/>
                </motion.button>
            </div>
        </div>
    );
};

export default OrderSummary;